import React from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// Betűországmeséi logó: a public/ mappában lévő logo.jpeg
const betuorszagmeseiLogo = `${process.env.PUBLIC_URL}/logo.jpeg`;

// Módosított SocialMediaLinkProps, hogy támogassa a top pozíciót
interface SocialMediaLinkProps {
  href: string;
  backgroundColor: string;
  icon: React.ReactElement;
  text: string;
  top: string; // Új prop a vertikális pozícióhoz
  textColor?: string; // szöveg színe (alapból fehér)
  hoverWidth?: string; // szélesség hoverkor (alapból 150px)
}

const SocialMediaLink: React.FC<SocialMediaLinkProps> = ({
  href,
  backgroundColor,
  icon,
  text,
  top,
  textColor = '#fff',
  hoverWidth = '150px',
}) => {
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        position: 'absolute', // Fontos: abszolút pozicionálás
        top: top, // A kapott top értéket használjuk
        right: 0, // Mindig a jobb oldalhoz igazítva
        display: 'flex',
        alignItems: 'center',
        width: '50px', // Alap szélesség, csak az ikonnak
        padding: '10px 0',
        backgroundColor: backgroundColor,
        color: textColor,
        textDecoration: 'none',
        borderRadius: '5px 0 0 5px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        transition: 'width 0.3s ease, box-shadow 0.3s ease', // Box-shadow tranzíció hozzáadása
        justifyContent: 'flex-end',

        '&:hover': {
          width: hoverWidth, // Szélesebb hoverre, felfedi a szöveget
          boxShadow: '0 0 10px rgba(0,0,0,0.3)',
        },

        '& .MuiSvgIcon-root': {
          fontSize: 30,
          flexShrink: 0,
          marginRight: '10px',
        },

        // A kép-ikon (logó) ugyanúgy viselkedik, mint az SVG-ikonok
        '& img': {
          width: 30,
          height: 30,
          flexShrink: 0,
          marginRight: '10px',
          objectFit: 'contain',
          borderRadius: '50%',
        },

        '& .MuiTypography-root': {
          opacity: 0,
          width: 0,
          overflow: 'hidden',
          transition: 'opacity 0.3s ease, width 0.3s ease',
          marginRight: 0,
        },

        '&:hover .MuiTypography-root': {
          opacity: 1,
          width: 'calc(100% - 55px)', // A teljes szélesség (150px) - az ikon szélessége (kb. 30px + 10px jobbmargó + 10px balmargó = 50px)
        },
      }}
    >
      <Typography variant="body1" sx={{ flexShrink: 0 }}>
        {text}
      </Typography>
      {icon}
    </MuiLink>
  );
};

const SocialMediaSidebar: React.FC = () => {
  const facebookPageLink = 'https://www.facebook.com/csillagtanoda';
  const instagramPageLink = 'https://www.instagram.com/csillagtanoda';
  const betuorszagmeseiLink = 'https://betuorszagmesei.csillagtanoda.hu/';

  // Meghatározzuk az egyes elemek 'top' pozícióját
  // Feltételezzük, hogy egy elem magassága kb. 50px (40px tartalom + 10px padding felül/alul)
  // és 2px marginBottom volt korábban, amit most beépítünk a top értékbe
  const linkHeight = 52; // 10px padding top + 10px padding bottom + 30px icon height + 2px margin-bottom (ami most a top értékben van)

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '150px', // Max szélesség, hogy a kinyúló elemek elférjenek
        height: `${linkHeight * 3}px`, // A linkek összesített magassága (3 link * linkmagasság)
        zIndex: 1000,
        // Ezt a Box-ot fogjuk használni referenciapontként az abszolút pozicionáláshoz
        // A display: 'flex' és flexDirection: 'column' már nem releváns itt
        // mert a gyerek elemek abszolút pozíciójúak lesznek.
      }}
    >
      <SocialMediaLink
        href={facebookPageLink}
        backgroundColor="#1877F2"
        icon={<FacebookIcon />}
        text="Facebook"
        top="0px" // Első elem a tetején
      />
      <SocialMediaLink
        href={instagramPageLink}
        backgroundColor="#E4405F"
        icon={<InstagramIcon />}
        text="Instagram"
        top={`${linkHeight}px`} // Második elem az első alatt
      />
      <SocialMediaLink
        href={betuorszagmeseiLink}
        backgroundColor="#FCC632" // arculati sárga
        textColor="#120A39" // sötét szöveg a sárgán
        hoverWidth="210px" // hosszabb név, ezért szélesebb
        icon={<img src={betuorszagmeseiLogo} alt="Betűországmeséi" />}
        text="Betűországmeséi"
        top={`${linkHeight * 2}px`} // Harmadik elem a második alatt
      />
    </Box>
  );
};

export default SocialMediaSidebar;